import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { apiService } from '../services/api';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [requiresAcademicQuestionnaire, setRequiresAcademicQuestionnaire] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Try to check auth status with a lightweight endpoint
        const response = await fetch('/api/auth-check', {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.authenticated) {
            setIsAuthenticated(true);
            setRequiresAcademicQuestionnaire(Boolean(data.academic_required));
          } else {
            setIsAuthenticated(false);
          }
        } else if (response.status === 401 || response.status === 403) {
          setIsAuthenticated(false);
        } else {
          // For any other status, assume not authenticated
          setIsAuthenticated(false);
        }
      } catch (error: any) {
        // Network error or other issue - assume not authenticated
        console.error('ProtectedRoute: Error checking auth:', error);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login with return path
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiresAcademicQuestionnaire && location.pathname !== '/academic-questionnaire') {
    return <Navigate to="/academic-questionnaire" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
